import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { api, API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/Layout";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, Tooltip, Legend } from "recharts";

const AXIS_LABELS = { position: "Posizione", margin: "Margine", legal: "Legale", documentation: "Documenti", duration: "Tempo", interest: "Interesse" };
const STAGE_LABELS = { lead: "Lead", analysis: "Analisi", contact: "Contatto", documentation: "Documenti", proposal: "Proposta", negotiation: "Trattativa", agreement: "Accordo", closed: "Chiuso" };
const COLORS = ["#2563EB", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444"];

export default function PropertyCompare() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);

  useEffect(() => {
    if (ids.length < 2) return;
    api.get("/properties-compare", { params: { ids: ids.join(",") } }).then(({ data }) => setItems(data.items));
  }, [params]);

  if (ids.length < 2) return <div className="text-muted-foreground text-sm">Seleziona almeno 2 immobili per il confronto.</div>;

  // Merge radar data
  const radarData = ["position", "margin", "legal", "documentation", "duration", "interest"].map((axis) => {
    const row = { axis: AXIS_LABELS[axis] };
    items.forEach((it, i) => { row[it.property.title] = it.score.radar[axis]; });
    return row;
  });

  const remove = (pid) => {
    const next = ids.filter((x) => x !== pid);
    if (next.length < 2) navigate("/properties");
    else navigate(`/compare?ids=${next.join(",")}`);
  };

  return (
    <div className="space-y-6" data-testid="compare-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/properties" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="w-4 h-4" /> Torna agli immobili
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Confronto immobili</h1>
          <p className="text-muted-foreground text-sm mt-1">Confronta {items.length} opportunità fianco a fianco.</p>
        </div>
      </div>

      {/* Radar comparison */}
      <Card className="border-border/60 p-6">
        <h3 className="font-heading text-lg font-medium mb-4">Radar comparativo</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              {items.map((it, i) => (
                <Radar key={it.property.id} name={it.property.title} dataKey={it.property.title}
                  stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.15} strokeWidth={2} />
              ))}
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Side-by-side cards */}
      <div className={`grid gap-4 ${items.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
        {items.map((it) => {
          const p = it.property; const s = it.score;
          const scoreColor = s.total >= 75 ? "text-emerald-600" : s.total >= 50 ? "text-blue-600" : s.total >= 25 ? "text-amber-600" : "text-rose-600";
          return (
            <Card key={p.id} className="border-border/60 overflow-hidden" data-testid={`compare-card-${p.id}`}>
              <div className="h-48 bg-muted relative">
                {p.hero_image ? <img src={p.hero_image.startsWith("http") ? p.hero_image : `${API}${p.hero_image}`} alt={p.title} className="w-full h-full object-cover" /> : null}
                <button onClick={() => remove(p.id)} className="absolute top-2 right-2 bg-background/90 backdrop-blur w-8 h-8 rounded-md flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground" data-testid={`remove-${p.id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/properties/${p.id}`} className="font-heading font-semibold text-lg hover:text-primary">{p.title}</Link>
                    <StatusBadge status={p.status} />
                  </div>
                  <p className="text-xs text-muted-foreground">{p.address}, {p.city}</p>
                </div>

                <div className={`flex items-baseline gap-2 ${scoreColor}`}>
                  <span className="font-heading text-4xl font-semibold tabular-nums">{s.total}</span>
                  <span className="text-xs text-muted-foreground">/ 100</span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="Prezzo" value={`€ ${fmt(p.price)}`} />
                  <Stat label="Valore stimato" value={`€ ${fmt(p.estimated_value)}`} />
                  <Stat label="Margine" value={`€ ${fmt(p.potential_margin || (p.estimated_value - p.price))}`} accent="text-emerald-600" />
                  <Stat label="ROI" value={`${s.margin_pct}%`} />
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <Meta label="📸 Foto" value={it.photos} />
                  <Meta label="📑 Documenti" value={it.docs_uploaded} />
                  <Meta label="💼 Fase" value={STAGE_LABELS[it.deal_stage] || "—"} />
                </div>

                {s.reasons.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Punti di forza</div>
                    <div className="flex flex-wrap gap-1">
                      {s.reasons.map((r, i) => <Badge key={i} className="bg-emerald-100 text-emerald-700 border-transparent text-[10px]">{r}</Badge>)}
                    </div>
                  </div>
                )}
                {s.suggestions.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Aree di miglioramento</div>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      {s.suggestions.map((sg, i) => <li key={i} className="flex items-start gap-1"><span className="text-amber-600">↑ {sg.gain}</span> {sg.text}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function fmt(n) { return Intl.NumberFormat("it-IT").format(Math.round(n || 0)); }
function Stat({ label, value, accent }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-heading font-semibold tabular-nums ${accent || ""}`}>{value}</div></div>;
}
function Meta({ label, value }) {
  return <div className="bg-muted rounded-md px-2 py-1.5 text-center"><div className="text-muted-foreground text-[10px]">{label}</div><div className="font-medium tabular-nums truncate">{value}</div></div>;
}
